/*
 *  Klaviyo BIS
 *    Controls functionality for the bis-widget. Subscribes email to Klaviyo
 *    BIS flow.
 *
 *  Version
 *    1.0.0 - 2022/07/08
 */

import { t, Variant } from "@process-creative/slate-theme-tools";
import { CLASS_HAS_ERRORS, CLASS_HIDDEN, CLASS_VISIBLE } from "../../tools/constants/classes";
import { ProductForm } from "./product-form";
import { EventVariantChanged, EVT_VARIANT_CHANGED } from "./variant-selector";

// Selectors
export const SELECTOR_BIS_CONTAINER = '[data-klaviyo-bis-container]';
const SELECTOR_BIS_INPUT_CONTAINER = '[data-klaviyo-bis-input-container]';
const SELECTOR_BIS_INPUT = '[data-klaviyo-bis-input]';
const SELECTOR_BIS_SUBMIT = '[data-klaviyo-bis-submit]';

const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/onsite/components/back-in-stock/subscribe';

// Strings
const STR_SUBMIT = t('products.back_in_stock.submit');
const STR_SUBMITTING = t('products.back_in_stock.submitting');
const STR_SUBMITTED = t('products.back_in_stock.submitted');
const STR_BTN_ERROR = t('products.back_in_stock.btn_error');

export class KlaviyoBackInStock {

  private container:HTMLElement;
  private formInstance:ProductForm;
  private template:HTMLElement;
  private inputElement:HTMLInputElement|null = null;
  private submitElement:HTMLButtonElement|null = null;

  constructor(container:HTMLElement, formInstance:ProductForm) {
    this.container = container;
    this.formInstance = formInstance;
    this.template = this.formInstance.container;
    this.inputElement = this.container.querySelector(SELECTOR_BIS_INPUT);
    this.submitElement = this.container.querySelector(SELECTOR_BIS_SUBMIT);
   
    // Set initial visibility
    const initialVariant = this?.formInstance?.swatches?.getVariant();
    if(initialVariant) this.setVisibility(initialVariant);

    this.attachEvents();
  }

  private onVariantChanged(e:Event) {
    const customEvent = e as EventVariantChanged;
    const variant = customEvent?.detail?.newVariant;
    this.setVisibility(variant);
  }

  private setVisibility(variant:Variant) {

    if((!variant.available || (variant.inventory_quantity && variant.inventory_quantity <= 0))) {
      this.container.classList.add(CLASS_VISIBLE);
      this.formInstance.btnAdd?.classList.add(CLASS_HIDDEN);
      
    } else {
      this.container.classList.remove(CLASS_VISIBLE);
      this.formInstance.btnAdd?.classList.remove(CLASS_HIDDEN);
    }
  }

  private async submitBackInStock(id:number, email:string) {
    try {
      if (!this.isKlaviyoEnabled()) throw('Klaviyo is not enabled');

        // @ts-ignore
        const key = window.klaviyoModulesObject.companyId;

        try {
          const response = await fetch(KLAVIYO_ENDPOINT, {
            method: 'POST',
            headers: {
              'accept': 'application/json',
            },
            body: new URLSearchParams({
              a: key,
              email: email,
              variant: `${id}`,
              platform: 'shopify'
            })
          });
          return ({ successful: response.ok, response: response });
        } catch (responseError) {
          return ({ successful: false, error: responseError });
        }
    } catch (error) {
      console.warn(error);
      return ({ successful: false, error: error });
    }
  }

  private isKlaviyoEnabled() {
    // @ts-ignore
    return (window.klaviyoModulesObject && window.klaviyoModulesObject.companyId);
  }

  private emailIsValid (email:string) {
    return /\S+@\S+\.\S+/.test(email)
  }

  private async onSubmit(e:Event) {
    e.preventDefault();

    try {
      if(!this.submitElement || !this.inputElement) throw 'Submit or input element is missing';

      const email = this.inputElement?.value;
      const variant = this.formInstance.swatches?.getVariant();
      const id = variant?.id;
      const hasEmail = email && this.emailIsValid(email);

      // Set email error state
      let errorEmailElement = this.container.querySelector<HTMLElement>(SELECTOR_BIS_INPUT_CONTAINER);
      if(!errorEmailElement) errorEmailElement = this.inputElement;
      if(!hasEmail) {
        errorEmailElement?.classList.add(CLASS_HAS_ERRORS);
        this.inputElement?.focus();
      } else {
        errorEmailElement?.classList.remove(CLASS_HAS_ERRORS);
      }

      if(!hasEmail || !id) throw 'Valid email or variant id is missing';

      this.submitElement.innerHTML = `<span>${STR_SUBMITTING}</span>`;

      // Submit to Klaviyo
      await this.submitBackInStock(id, email).then(response => {
        if(!this.submitElement) throw 'Submit element is missing';

        if(response.successful) {
          this.submitElement.innerHTML = `<span>${STR_SUBMITTED}</span>`;
        } else if(response.error) {
          this.submitElement.innerHTML = `<span>${STR_BTN_ERROR}</span>`;
          throw response.error;
        }
      });

      // Reset button state
      await new Promise(resolve => setTimeout(resolve, 4000));
      this.submitElement.innerHTML = `<span>${STR_SUBMIT}</span>`;
      this.inputElement.value = '';
    } catch (error) {
      console.warn(error);
    }
  }

  public attachEvents() {

    this.template.addEventListener(EVT_VARIANT_CHANGED, this.onVariantChanged.bind(this));
    this.submitElement?.addEventListener('click', this.onSubmit.bind(this));
  }

  public detatchEvents() {
    this.template.removeEventListener(EVT_VARIANT_CHANGED, this.onVariantChanged.bind(this));
    this.submitElement?.removeEventListener('click', this.onSubmit.bind(this));
  }
}
